import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';
import { Colour } from '../../Validators';

export interface IConfigThemeEntityAttributes extends IModelAttributes {
	homepageBackgroundImageId: string;
	homepageBackgroundImage: Blob;
	loginBackgroundImageId: string;
	loginBackgroundImage: Blob;
	registerBackgroundImageId: string;
	registerBackgroundImage: Blob;
	convertGuestBackgroundImageId: string;
	convertGuestBackgroundImage: Blob;
	bookingWizardBackgroundImageId: string;
	bookingWizardBackgroundImage: Blob;
	eventBookingWizardBackgroundImageId: string;
	eventBookingWizardBackgroundImage: Blob;
	resetPasswordBackgroundImageId: string;
	resetPasswordBackgroundImage: Blob;
	customerRegistrationSideImageId: string;
	customerRegistrationSideImage: Blob;
	changeEmailSideImageId: string;
	changeEmailSideImage: Blob;
	errorPageBackgroundImageId: string;
	errorPageBackgroundImage: Blob;
	invalidTokenBackgroundImageId: string;
	invalidTokenBackgroundImage: Blob;
	bookingSuccessBackgroundImageId: string;
	bookingSuccessBackgroundImage: Blob;
	bookingCancelSuccessBackgroundImageId: string;
	bookingCancelSuccessBackgroundImage: Blob;
	pageNotFoundBackgroundImageId: string;
	pageNotFoundBackgroundImage: Blob;
	claimVoucherBackgroundImageId: string;
	claimVoucherBackgroundImage: Blob;
	eventsBackgroundImageId: string;
	eventsBackgroundImage: Blob;
	bookingSuccessPromoCardImageId: string;
	bookingSuccessPromoCardImage: Blob;
	emailHeaderImageId: string;
	emailHeaderImage: Blob;
	brandColourPrimary: string;
	brandColourPrimary2: string;
	brandColourSecondary: string;
	brandColourSecondary2: string;
	brandLogoPrimaryId: string;
	brandLogoPrimary: Blob;
	brandLogoSecondaryId: string;
	brandLogoSecondary: Blob;
	brandFaviconId: string;
	brandFavicon: Blob;
	textOnPrimaryColour: Enums.fontColour;
	textOnSecondaryColour: Enums.fontColour;
	marketingSiteURL: string;
	brandLogoPrimaryLightId: string;
	brandLogoPrimaryLight: Blob;
	brandLogoSecondaryLightId: string;
	brandLogoSecondaryLight: Blob;
	highAvailabilityColour: string;
	lowAvailabilityColour: string;
	noAvailabilityColour: string;
	paidTagColour: string;
	invoicedTagColour: string;
	awaitingTagColour: string;
	cancelledTagColour: string;
	newTagColour: string;
	textOnPaidTag: Enums.fontColour;
	textOnCancelledTag: Enums.fontColour;
	textOnInvoicedTag: Enums.fontColour;
	textOnAwaitingTag: Enums.fontColour;
	textOnNewTag: Enums.fontColour;

	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
}
@entity('ConfigThemeEntity', 'Config Theme')
export default class ConfigThemeEntity extends Model implements IConfigThemeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	// ── Per-page background images ──────────────────────────────────────

	@observable
	@attribute({ file: 'homepageBackgroundImage' })
	public homepageBackgroundImageId: string;

	@observable
	public homepageBackgroundImage: Blob;

	@observable
	@attribute({ file: 'loginBackgroundImage' })
	public loginBackgroundImageId: string;

	@observable
	public loginBackgroundImage: Blob;

	@observable
	@attribute({ file: 'registerBackgroundImage' })
	public registerBackgroundImageId: string;

	@observable
	public registerBackgroundImage: Blob;

	@observable
	@attribute({ file: 'convertGuestBackgroundImage' })
	public convertGuestBackgroundImageId: string;

	@observable
	public convertGuestBackgroundImage: Blob;

	@observable
	@attribute({ file: 'bookingWizardBackgroundImage' })
	public bookingWizardBackgroundImageId: string;

	@observable
	public bookingWizardBackgroundImage: Blob;

	@observable
	@attribute({ file: 'eventBookingWizardBackgroundImage' })
	public eventBookingWizardBackgroundImageId: string;

	@observable
	public eventBookingWizardBackgroundImage: Blob;

	@observable
	@attribute({ file: 'resetPasswordBackgroundImage' })
	public resetPasswordBackgroundImageId: string;

	@observable
	public resetPasswordBackgroundImage: Blob;

	@observable
	@attribute({ file: 'customerRegistrationSideImage' })
	public customerRegistrationSideImageId: string;

	@observable
	public customerRegistrationSideImage: Blob;

	@observable
	@attribute({ file: 'changeEmailSideImage' })
	public changeEmailSideImageId: string;

	@observable
	public changeEmailSideImage: Blob;

	@observable
	@attribute({ file: 'errorPageBackgroundImage' })
	public errorPageBackgroundImageId: string;

	@observable
	public errorPageBackgroundImage: Blob;

	@observable
	@attribute({ file: 'invalidTokenBackgroundImage' })
	public invalidTokenBackgroundImageId: string;

	@observable
	public invalidTokenBackgroundImage: Blob;

	@observable
	@attribute({ file: 'bookingSuccessBackgroundImage' })
	public bookingSuccessBackgroundImageId: string;

	@observable
	public bookingSuccessBackgroundImage: Blob;

	@observable
	@attribute({ file: 'bookingCancelSuccessBackgroundImage' })
	public bookingCancelSuccessBackgroundImageId: string;

	@observable
	public bookingCancelSuccessBackgroundImage: Blob;

	@observable
	@attribute({ file: 'pageNotFoundBackgroundImage' })
	public pageNotFoundBackgroundImageId: string;

	@observable
	public pageNotFoundBackgroundImage: Blob;

	@observable
	@attribute({ file: 'claimVoucherBackgroundImage' })
	public claimVoucherBackgroundImageId: string;

	@observable
	public claimVoucherBackgroundImage: Blob;

	@observable
	@attribute({ file: 'eventsBackgroundImage' })
	public eventsBackgroundImageId: string;

	@observable
	public eventsBackgroundImage: Blob;

	@observable
	@attribute({ file: 'bookingSuccessPromoCardImage' })
	public bookingSuccessPromoCardImageId: string;

	@observable
	public bookingSuccessPromoCardImage: Blob;

	@observable
	@attribute({ file: 'emailHeaderImage' })
	public emailHeaderImageId: string;

	@observable
	public emailHeaderImage: Blob;

	@observable
	@Colour()
	@attribute()
	public brandColourPrimary: string;

	@observable
	@attribute<ConfigThemeEntity, string>()
	public brandColourPrimary2: string;

	@observable
	@Colour()
	@attribute()
	public brandColourSecondary: string;

	@observable
	@attribute<ConfigThemeEntity, string>()
	public brandColourSecondary2: string;

	@observable
	@attribute({ file: 'brandLogoPrimary' })
	public brandLogoPrimaryId: string;

	@observable
	public brandLogoPrimary: Blob;

	@observable
	@attribute({ file: 'brandLogoSecondary' })
	public brandLogoSecondaryId: string;

	@observable
	public brandLogoSecondary: Blob;

	@observable
	@attribute({ file: 'brandFavicon' })
	public brandFaviconId: string;

	@observable
	public brandFavicon: Blob;

	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnPrimaryColour: Enums.fontColour;

	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnSecondaryColour: Enums.fontColour;

	@observable
	@attribute<ConfigThemeEntity, string>()
	public marketingSiteURL: string;

	@observable
	@attribute({ file: 'brandLogoPrimaryLight' })
	public brandLogoPrimaryLightId: string;

	@observable
	public brandLogoPrimaryLight: Blob;

	@observable
	@attribute({ file: 'brandLogoSecondaryLight' })
	public brandLogoSecondaryLightId: string;

	@observable
	public brandLogoSecondaryLight: Blob;

	/**
	 * The colour that will appears when there is high availability on a trip card.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public highAvailabilityColour: string;

	/**
	 * The colour that will appears when there is low availability on a trip card.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public lowAvailabilityColour: string;

	/**
	 * The colour that will appears when there is no availability on a trip card.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public noAvailabilityColour: string;

	/**
	 * The colour that paid tags use throughout the application.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public paidTagColour: string;

	/**
	 * The colour that invoiced tags use throughout the application.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public invoicedTagColour: string;

	/**
	 * The awaiting tag colour that will appear throughout the application.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public awaitingTagColour: string;

	/**
	 * The cancelled tag colour that will appear throughout the application.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public cancelledTagColour: string;

	/**
	 * The colour of the new tag that appears around the application.
	 */
	@observable
	@attribute<ConfigThemeEntity, string>()
	public newTagColour: string;

	/**
	 * The text colour that appears on the paid tag colour.
	 */
	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnPaidTag: Enums.fontColour;

	/**
	 * The text colour that appears on the cancelled tag colour.
	 */
	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnCancelledTag: Enums.fontColour;

	/**
	 * The text colour that appears on the invoiced tag colour.
	 */
	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnInvoicedTag: Enums.fontColour;

	/**
	 * The text colour that appears on the awaiting tag colour.
	 */
	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnAwaitingTag: Enums.fontColour;

	/**
	 * The text colour that appears on the new tag colour.
	 */
	@observable
	@attribute<ConfigThemeEntity, Enums.fontColour>()
	public textOnNewTag: Enums.fontColour;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation?: Models.OrganisationEntity;

	constructor(attributes?: Partial<IConfigThemeEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IConfigThemeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			// Per-page background images
			const imageFields = [
				'homepageBackgroundImage',
				'loginBackgroundImage',
				'registerBackgroundImage',
				'convertGuestBackgroundImage',
				'bookingWizardBackgroundImage',
				'eventBookingWizardBackgroundImage',
				'resetPasswordBackgroundImage',
				'customerRegistrationSideImage',
				'changeEmailSideImage',
				'errorPageBackgroundImage',
				'invalidTokenBackgroundImage',
				'bookingSuccessBackgroundImage',
				'bookingCancelSuccessBackgroundImage',
				'pageNotFoundBackgroundImage',
				'claimVoucherBackgroundImage',
				'eventsBackgroundImage',
				'bookingSuccessPromoCardImage',
				'emailHeaderImage',
			] as const;
			for (const field of imageFields) {
				const idField = `${field}Id` as keyof IConfigThemeEntityAttributes;
				if (attributes[field as keyof IConfigThemeEntityAttributes] !== undefined) {
					(this as any)[field] = attributes[field as keyof IConfigThemeEntityAttributes];
				}
				if (attributes[idField] !== undefined) {
					(this as any)[idField] = attributes[idField];
				}
			}
			if (attributes.brandColourPrimary !== undefined) {
				this.brandColourPrimary = attributes.brandColourPrimary;
			}
			if (attributes.brandColourPrimary2 !== undefined) {
				this.brandColourPrimary2 = attributes.brandColourPrimary2;
			}
			if (attributes.brandColourSecondary !== undefined) {
				this.brandColourSecondary = attributes.brandColourSecondary;
			}
			if (attributes.brandColourSecondary2 !== undefined) {
				this.brandColourSecondary2 = attributes.brandColourSecondary2;
			}
			if (attributes.brandLogoPrimary !== undefined) {
				this.brandLogoPrimary = attributes.brandLogoPrimary;
			}
			if (attributes.brandLogoPrimaryId !== undefined) {
				this.brandLogoPrimaryId = attributes.brandLogoPrimaryId;
			}
			if (attributes.brandLogoSecondary !== undefined) {
				this.brandLogoSecondary = attributes.brandLogoSecondary;
			}
			if (attributes.brandLogoSecondaryId !== undefined) {
				this.brandLogoSecondaryId = attributes.brandLogoSecondaryId;
			}
			if (attributes.brandFavicon !== undefined) {
				this.brandFavicon = attributes.brandFavicon;
			}
			if (attributes.brandFaviconId !== undefined) {
				this.brandFaviconId = attributes.brandFaviconId;
			}
			if (attributes.textOnPrimaryColour !== undefined) {
				this.textOnPrimaryColour = attributes.textOnPrimaryColour;
			}
			if (attributes.textOnSecondaryColour !== undefined) {
				this.textOnSecondaryColour = attributes.textOnSecondaryColour;
			}
			if (attributes.marketingSiteURL !== undefined) {
				this.marketingSiteURL = attributes.marketingSiteURL;
			}
			if (attributes.brandLogoPrimaryLight !== undefined) {
				this.brandLogoPrimaryLight = attributes.brandLogoPrimaryLight;
			}
			if (attributes.brandLogoPrimaryLightId !== undefined) {
				this.brandLogoPrimaryLightId = attributes.brandLogoPrimaryLightId;
			}
			if (attributes.brandLogoSecondaryLight !== undefined) {
				this.brandLogoSecondaryLight = attributes.brandLogoSecondaryLight;
			}
			if (attributes.brandLogoSecondaryLightId !== undefined) {
				this.brandLogoSecondaryLightId = attributes.brandLogoSecondaryLightId;
			}
			if (attributes.highAvailabilityColour !== undefined) {
				this.highAvailabilityColour = attributes.highAvailabilityColour;
			}
			if (attributes.lowAvailabilityColour !== undefined) {
				this.lowAvailabilityColour = attributes.lowAvailabilityColour;
			}
			if (attributes.noAvailabilityColour !== undefined) {
				this.noAvailabilityColour = attributes.noAvailabilityColour;
			}
			if (attributes.paidTagColour !== undefined) {
				this.paidTagColour = attributes.paidTagColour;
			}
			if (attributes.invoicedTagColour !== undefined) {
				this.invoicedTagColour = attributes.invoicedTagColour;
			}
			if (attributes.awaitingTagColour !== undefined) {
				this.awaitingTagColour = attributes.awaitingTagColour;
			}
			if (attributes.cancelledTagColour !== undefined) {
				this.cancelledTagColour = attributes.cancelledTagColour;
			}
			if (attributes.newTagColour !== undefined) {
				this.newTagColour = attributes.newTagColour;
			}
			if (attributes.textOnPaidTag !== undefined) {
				this.textOnPaidTag = attributes.textOnPaidTag;
			}
			if (attributes.textOnCancelledTag !== undefined) {
				this.textOnCancelledTag = attributes.textOnCancelledTag;
			}
			if (attributes.textOnInvoicedTag !== undefined) {
				this.textOnInvoicedTag = attributes.textOnInvoicedTag;
			}
			if (attributes.textOnAwaitingTag !== undefined) {
				this.textOnAwaitingTag = attributes.textOnAwaitingTag;
			}
			if (attributes.textOnNewTag !== undefined) {
				this.textOnNewTag = attributes.textOnNewTag;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
				}
			}
		}
	}
}
